import {
  Briefcase,
  Mail,
  MapPin,

  Phone,
  User,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ParentInfo, StudentProfileDetails } from './profileData';

interface ProfileParentGuardianTabProps {
  details: StudentProfileDetails;
  /** Whose record this is, when the reader is not the child themselves. */
  childName?: string;
}

/**
 * One detail line inside a parent's card.
 *
 * `null` prints a dash. The office holds no number for some mothers and no
 * email for most, and an empty line reads as a page that failed to load.
 */
function DetailRow({
  icon,
  label,
  value,
  mono = false,
  last = false,
  trailing,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null;
  mono?: boolean;
  last?: boolean;
  trailing?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex items-start py-1',
        !last && 'border-b border-slate-100/60 dark:border-white/5',
      )}
    >
      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 font-medium w-28 sm:w-44 shrink-0 pt-0.5">
        {icon}
        <span>{label}</span>
      </div>
      <span className="w-4 sm:w-6 text-center text-slate-400 dark:text-slate-500 font-normal shrink-0">
        :
      </span>
      <div
        className={cn(
          'flex-1 flex items-center gap-1.5 font-bold text-slate-700 dark:text-white text-left',
          mono ? 'font-mono break-all' : 'break-words',
        )}
      >
        <span>{value ?? '—'}</span>
        {value && trailing}
      </div>
    </div>
  );
}

/**
 * A parent or guardian, as the office holds them.
 *
 * One component drawn twice rather than two copies of the same hundred lines
 * (CLAUDE.md §3) — the father's card and the mother's differ only in the
 * colour of the initial.
 */
function ParentCard({
  parent,
  initial,
  tone,
  badgeTone,
}: {
  parent: ParentInfo;
  initial: string;
  tone: string;
  badgeTone: string;
}) {
  return (
    <div className="rounded-xl bg-slate-50/90 dark:bg-slate-800/50 border border-slate-200/80 dark:border-slate-700/80 p-4 flex flex-col gap-3">
      <div className="flex items-center gap-3 border-b border-slate-200/60 dark:border-slate-700/60 pb-2.5">
        <div
          className={cn(
            'h-10 w-10 rounded-full text-white font-bold text-sm flex items-center justify-center shrink-0 shadow-xs',
            tone,
          )}
        >
          {initial}
        </div>
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="text-sm sm:text-base font-bold text-slate-900 dark:text-white truncate">
            {parent.name}
          </span>
          <span
            className={cn(
              'px-2 py-0.5 rounded-full text-[10px] font-bold border',
              badgeTone,
            )}
          >
            {parent.relation}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-1.5 text-xs sm:text-base">
        <DetailRow
          icon={<User className="h-4 w-4 text-slate-400 dark:text-slate-500 shrink-0" />}
          label="Relationship"
          value={parent.relation}
        />
        <DetailRow
          icon={<Phone className="h-4 w-4 text-slate-400 dark:text-slate-500 shrink-0" />}
          label="Phone Number"
          value={parent.phone}
          mono
        />
        <DetailRow
          icon={<Mail className="h-4 w-4 text-slate-400 dark:text-slate-500 shrink-0" />}
          label="Email Address"
          value={parent.email}
        />
        <DetailRow
          icon={<Briefcase className="h-4 w-4 text-slate-400 dark:text-slate-500 shrink-0" />}
          label="Occupation"
          value={parent.occupation}
        />
        <DetailRow
          icon={<MapPin className="h-4 w-4 text-slate-400 dark:text-slate-500 shrink-0" />}
          label="Address"
          value={parent.address}
          last
        />
      </div>
    </div>
  );
}

/**
 * The child's family, as the school office recorded it at admission.
 *
 * Read-only. A change of address or of a parent's mobile number is a
 * correction to the admission record, which the office makes — the banner at
 * the foot of the screen says where to take one.
 */
export function ProfileParentGuardianTab({ details, childName }: ProfileParentGuardianTabProps) {
  const hasParents = Boolean(details.father || details.mother);

  return (
    <div className="flex flex-col gap-5 animate-fade-in">
      {/* 1. Main Parent / Guardian Information Card */}
      <div className="rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 p-4 shadow-xs flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-white/10 pb-3.5">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-purple-50 text-purple-600 dark:bg-purple-950/60 dark:text-purple-400 border border-purple-200/70 dark:border-purple-800/60 shrink-0">
              <Users className="h-4.5 w-4.5" />
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                Parent / Guardian Information
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5">
                Details of parents or guardians for communication and emergency contact.
              </p>
            </div>
          </div>
        </div>

        {hasParents ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {details.father && (
              <ParentCard
                parent={details.father}
                initial="F"
                tone="bg-blue-500"
                badgeTone="bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-200 border-blue-200 dark:border-blue-800"
              />
            )}
            {details.mother && (
              <ParentCard
                parent={details.mother}
                initial="M"
                tone="bg-purple-500"
                badgeTone="bg-purple-100 text-purple-800 dark:bg-purple-900/60 dark:text-purple-200 border-purple-200 dark:border-purple-800"
              />
            )}
          </div>
        ) : (
          <p className="py-6 text-center text-sm font-medium text-slate-500 dark:text-slate-400">
            Your school office has not recorded a parent or guardian yet.
          </p>
        )}
      </div>

      {/* 2. Additional Guardian Card */}
      <div className="rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 p-4 shadow-xs flex flex-col gap-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-white/10 pb-3.5">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-400 border border-indigo-200/70 dark:border-indigo-800/60 shrink-0">
              <Phone className="h-4.5 w-4.5" />
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                Additional Guardian
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5">
                Whoever the office was asked to call when {childName ? `${childName}'s` : 'your'}{' '}
                parents cannot be reached.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2.5">
          {details.emergencyContacts.length === 0 && (
            <p className="py-6 text-center text-sm font-medium text-slate-500 dark:text-slate-400">
              No additional guardian is on {childName ? `${childName}'s` : 'your'} record.
            </p>
          )}

          {details.emergencyContacts.map((contact) => (
            <div
              key={contact.id}
              className="p-3.5 rounded-xl bg-slate-50/90 dark:bg-slate-800/50 border border-slate-200/70 dark:border-slate-700/70 flex flex-col md:flex-row items-start md:items-center gap-4"
            >
              <div className="flex items-center gap-3 shrink-0 min-w-0 sm:min-w-[200px] lg:min-w-[240px]">
                <div className="h-10 w-10 rounded-full bg-blue-600 text-white font-bold text-sm flex items-center justify-center shrink-0 shadow-xs">
                  {contact.name.charAt(0)}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                    {contact.name}
                  </span>
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-200 border border-blue-200 dark:border-blue-800">
                    {contact.relation}
                  </span>
                </div>
              </div>

              <div className="flex items-center flex-wrap gap-4 sm:gap-8 md:gap-12 lg:gap-16">
                <div className="flex flex-col min-w-0">
                  <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium">
                    Phone Number
                  </span>
                  <span className="text-xs sm:text-base font-bold text-slate-800 dark:text-white font-mono mt-0.5 break-all">
                    {contact.phone ?? '—'}
                  </span>
                </div>

                <div className="flex flex-col min-w-0">
                  <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium">
                    Relationship
                  </span>
                  <span className="text-xs sm:text-base font-bold text-slate-800 dark:text-white mt-0.5 break-words">
                    {contact.relation}
                  </span>
                </div>

                <div className="flex flex-col min-w-0">
                  <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium">
                    Address
                  </span>
                  <span
                    className="text-xs sm:text-base font-bold text-slate-800 dark:text-white mt-0.5 break-words"
                    title={contact.address}
                  >
                    {contact.address}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 3. Notice Banner: where a correction is made */}
      <div className="rounded-2xl bg-gradient-to-r from-blue-950/90 via-indigo-950/90 to-purple-950/90 text-white p-4 sm:p-5 shadow-xs flex items-center justify-between gap-4 border border-indigo-800/40 relative overflow-hidden">
        <div className="flex items-center gap-3.5 relative z-10">
          <div className="h-10 w-10 rounded-xl bg-blue-500/20 border border-blue-400/30 flex items-center justify-center text-blue-300 shrink-0">
            <Users className="h-5 w-5" />
          </div>
          <div className="flex flex-col">
            <h4 className="text-sm sm:text-base font-bold text-white">
              Keep your information up to date
            </h4>
            <p className="text-xs sm:text-sm text-indigo-200 mt-0.5">
              If there are any changes in your contact details, please inform the school office.
            </p>
          </div>
        </div>
        <Users className="h-20 w-20 text-white/5 absolute right-4 -bottom-4 pointer-events-none" />
      </div>
    </div>
  );
}
